import React, { useEffect } from 'react';
import { ShaderAnimation } from './ShaderAnimation';

const SubscriptionConfirm = () => {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* Shader Animation Background */}
      <div className="fixed inset-0 z-0">
        <ShaderAnimation />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20">

        <div className="relative max-w-2xl w-full">
          {/* Lozenge Badge */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <div className="inline-flex items-center px-6 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 backdrop-blur-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse" aria-hidden="true" />
              <span className="text-sm font-medium text-purple-100">Subscription Confirmed</span>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-12 shadow-2xl animate-slide-up">
            {/* Header */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center font-space-grotesk">
              You're in, welcome to the <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Antix crew</span>.
            </h1>

            {/* Body Copy */}
            <div className="text-slate-300 text-lg leading-relaxed space-y-4 mb-10">
              <p>
                Thanks for confirming your subscription. Your support genuinely means a lot. Antix started as my way to make live music simpler to enjoy — keeping track of tickets, discovering events, and connecting with friends.
              </p>
              <p>
                I'm building this solo, so every person who joins helps keep it moving. If you'd like to support the journey, you can do so on Ko-fi or follow along on Instagram.
              </p>
              <p className="text-slate-400 italic">
                Olly, founder of Antix
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ko-fi CTA */}
                <a
                  href="https://ko-fi.com/ollyhuntsman"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Support Antix on Ko-fi"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'cta_click', {
                        event_category: 'Donation',
                        event_label: 'kofi_confirm_page',
                      });
                    }
                  }}
                  className="group relative px-6 py-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl font-semibold text-white text-center transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/50 hover:scale-105"
                >
                  <span className="relative z-10">Support us on Ko-fi</span>
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                </a>

                {/* Instagram CTA */}
                <a
                  href="https://www.instagram.com/antix.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Antix on Instagram"
                  onClick={() => {
                    if (window.gtag) {
                      window.gtag('event', 'social_click', {
                        event_category: 'Social',
                        event_label: 'instagram_confirm_page',
                      });
                    }
                  }}
                  className="group relative px-6 py-4 bg-white/10 backdrop-blur-sm rounded-xl font-semibold text-white text-center border border-white/20 transition-all duration-300 hover:bg-white/20 hover:border-white/30 hover:scale-105"
                >
                  <span className="relative z-10">Follow us on Instagram</span>
                </a>
              </div>
            </div>

            {/* Home Link */}
            <div className="text-center pt-4 border-t border-white/10">
              <a
                href="https://www.antix.app"
                aria-label="Return to Antix homepage"
                onClick={() => {
                  if (window.gtag) {
                    window.gtag('event', 'navigation_click', {
                      event_category: 'Navigation',
                      event_label: 'home_from_confirm',
                    });
                  }
                }}
                className="inline-flex items-center text-slate-400 hover:text-white transition-colors duration-300 text-sm group"
              >
                <svg
                  className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Go home
              </a>
            </div>
          </div>

          {/* Privacy Policy Link - Outside the box, centered at bottom */}
          <div className="text-center mt-6">
            <a
              href="/privacy-policy"
              aria-label="View privacy policy"
              onClick={() => {
                if (window.gtag) {
                  window.gtag('event', 'legal_click', {
                    event_category: 'Legal',
                    event_label: 'privacy_from_confirm',
                  });
                }
              }}
              className="text-slate-500 hover:text-slate-300 transition-colors duration-300 text-xs"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SubscriptionConfirm;
